import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Observable, Subject,  } from 'rxjs';
import { addMonth } from '../../helper/custom-date-range-helper';
import { EThemeName, ThemeService } from '@wind-log_library/fe-component-library';
import { map, takeUntil } from 'rxjs/operators';
import { BsDatepickerConfig, BsLocaleService } from 'ngx-bootstrap/datepicker';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { addMonthButOneMs, outMonthDuration, round10MinHigh, round10MinLow } from 'app/shared/helper/date-format.helper';
import { TimezoneService } from 'app/shared/services/timezone/timezone.service';
import { LanguageService } from 'app/shared/services/language/language.service';

@Component({
  selector: 'app-custom-date-range',
  templateUrl: './custom-date-range.component.html',
  styleUrls: ['./custom-date-range.component.scss'],
  standalone: false,
})
export class CustomDateRangeComponent implements OnChanges, OnInit, OnDestroy {
  @Input() start: number;
  @Input() end: number;
  @Input() minTime: Date;
  @Input() ms?: number;
  @Input() openedPicker: boolean;
  @Input() isReport: boolean;
  @Input() roundTime: boolean = false;
  @Input() maxDateRange: number = 31; // Default max date range in days
  @Input() quarterly = false;

  @Output() setRange = new EventEmitter();
  @Output() apply = new EventEmitter();

  public isMeridian = false;
  public showSpinners = true;
  public startTime: Date;
  public endTime: Date;
  public bsRangeValue: Date[];
  public maxTime: Date = new Date();
  public bsConfig: Observable<Partial<BsDatepickerConfig>>;
  public showLastDataPointWarning: boolean = false;
  public warnMessage: string;

  private unsubscriber = new Subject();

  @ViewChild('rangeDates') rangeDates: ElementRef<HTMLElement>;

  constructor(
    private themeService: ThemeService,
    private translateService: TranslateService,
    private localeService: BsLocaleService,
    private timezoneService: TimezoneService,
    private languageService: LanguageService
  ) {}

  public ngOnInit(): void {
    const maxDateRange = this.quarterly ? 92 : this.maxDateRange;
    this.warnMessage = this.quarterly ? 'COMMON.MAX_RANGE_QUARTERLY_WARN_MESSAGE' : 'COMMON.MAX_RANGE_WARN_MESSAGE';
    this.bsConfig = this.themeService
      .getTheme()
      .pipe(
        map((theme: EThemeName) =>
          Object.assign({}, { containerClass: theme, dateInputFormat: 'DD MMM YYYY', maxDateRange })
        )
      );
    this.subscribeLanguageChange();
  }

  public get isValidRange(): boolean {
    const start = moment(this.startTime);
    let maxDays = start.daysInMonth();

    if (this.quarterly) {
      maxDays += moment(start).add(1, 'months').daysInMonth();
      maxDays += moment(start).add(2, 'months').daysInMonth();
    }
    const diff = this.endTime?.getTime() - this.startTime?.getTime();
    return diff > 0 && diff <= maxDays * 24 * 60 * 60 * 1000;
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.start?.currentValue && changes.start?.currentValue !== this.startTime) {
      this.startTime = new Date(this.start);
    }
    if (changes.end?.currentValue && changes.end?.currentValue !== this.endTime) {
      this.endTime = new Date(this.end);
    }
    if (
      changes.start?.currentValue &&
      changes.end?.currentValue &&
      changes.start?.currentValue !== this.startTime &&
      changes.end?.currentValue !== this.endTime
    ) {
      this.bsRangeValue = [this.startTime, this.endTime];
    }

    if (changes.minTime?.currentValue) {
      this.minTime = new Date(changes.minTime.currentValue);
    }

    if (changes.openedPicker?.currentValue) {
      this.rangeDates?.nativeElement?.click();
    }
    this.maxTime = new Date(this.timezoneService.unApplyTzFromDate(new Date().getTime()).valueOf());
  }

  public bsValueChange(event: [Date, Date]) {
    const numberOfMonths = this.quarterly ? 3 : 1
    this.startTime = event[0];
    this.endTime = outMonthDuration(...event, numberOfMonths) ? (this.isReport ? addMonthButOneMs(event[0], numberOfMonths) : addMonth(event[0])) : event[1];
    
    if (this.areDatesOnTheSameDay(this.endTime, this.maxTime)) {
      this.maxTime.setHours(23, 59, 59, 999);
    }
    this.timeChanged();
    if (this.bsRangeValue[0].getTime() !== this.startTime.getTime() || this.bsRangeValue[1].getTime() !== this.endTime.getTime()) {
      if (this.isReport) {
        this.endTime.setHours(23, 59, 59, 999);
      }
      this.bsRangeValue[0] = this.startTime;
      this.bsRangeValue[1] = this.endTime;
    }
  }

  public timeChanged() {
    if (this.startTime && this.endTime && this.roundTime) {
      if (this.startTime.getMinutes() % 10 !== 0) {
        let startTime = new Date(this.startTime);
        if (this.startTime.valueOf() >= this.minTime.valueOf()) startTime = round10MinLow(moment(this.startTime)).toDate();
        else startTime = this.minTime;
        this.bsRangeValue[0] = startTime;
        this.startTime = startTime;
      }
      if (this.endTime.getMinutes() % 10 !== 0) {
        let endTime = new Date(this.endTime);
        if (round10MinHigh(moment(this.endTime)).valueOf() <= this.maxTime.valueOf())
          endTime = round10MinHigh(moment(this.endTime)).toDate();
        else endTime = this.maxTime;
        this.bsRangeValue[1] = endTime;
        this.endTime = endTime;
      }
    }

    if (this.startTime?.getTime() === this.endTime?.getTime()) {
      this.startTime.setHours(0, 0, 0, 0);
      if (this.isReport) {
        this.endTime.setHours(23, 59, 59, 999);
      } else {
        this.endTime.setHours(23, 50);
      }
    }

    /* 
      Shows a warning message to inform the last data point of the day is not being
      included on the selection (only if 23:50 is selected as ending hour).
    */
    const endingHour = moment(this.endTime).hours();
    const endingMinutes = moment(this.endTime).minutes();
    this.showLastDataPointWarning = endingHour === 23 && endingMinutes === 50;
  }

  public timeStartChanged(event) {
    this.startTime ??= event;
    this.timeChanged();
  }

  public timeEndChanged(event) {
    this.endTime ??= event;
    this.timeChanged();
  }

  public onApply() {
    this.openedPicker = false;
    this.rangeDates?.nativeElement?.click();
    this.setRange.emit({ minSelectedTs: moment(this.startTime).valueOf(), maxSelectedTs: moment(this.endTime).valueOf() });
    this.apply.emit();
  }

  private subscribeLanguageChange() {
    this.languageService.updateCalendars(this.translateService.currentLang, this.localeService);
    this.translateService.onLangChange.pipe(takeUntil(this.unsubscriber)).subscribe((event) => {
      if (event) {
        this.languageService.updateCalendars(event.lang, this.localeService);
      }
    });
  }

  private areDatesOnTheSameDay(date1: Date, date2: Date): boolean {
    if (!date1 || !date2) {
      return false;
    }
    return date1.getFullYear() === date2.getFullYear() && date1.getMonth() === date2.getMonth() && date1.getDate() === date2.getDate();
  }

  ngOnDestroy() {
    this.unsubscriber.next(true);
    this.unsubscriber.complete();
  }

  get dateTimeFormat() {
    return this.isReport ? 'dmy' : 'dmyt';
  }
}
